import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [publicStats, setPublicStats] = useState(null);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/stats`).then(r => setPublicStats(r.data)).catch(() => {});
    axios.get(`${BACKEND_URL}/api/public/testimonials`).then(r => setTestimonials(r.data.testimonials || [])).catch(() => {});
  }, []);

  const handleGenerate = () => {
    if (searchQuery.trim()) {
      navigate('/dashboard', { state: { query: searchQuery } });
    } else {
      navigate('/register');
    }
  };

  // ONLY ACTIVE services
  const services = [
    {
      icon: '📝',
      title: 'Generación de Texto',
      description: 'Crea contenido profesional con múltiples modelos de IA.',
      link: '/dashboard',
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      icon: '🎨',
      title: 'Generación de Imágenes',
      description: 'Crea imágenes únicas con DALL-E 3.',
      link: '/dashboard',
      gradient: 'from-purple-500 to-pink-600'
    },
    {
      icon: '🤖',
      title: 'Chatbot Automatizado',
      description: 'Asistente IA multi-modelo con capacidades avanzadas.',
      link: '/autochatbot',
      gradient: 'from-cyan-500 to-blue-600',
      badge: 'Avanzado'
    },
    {
      icon: '💼',
      title: 'Simulador Financiero',
      description: 'Plantillas y simulaciones tributarias informativas.',
      link: '/accounting',
      gradient: 'from-emerald-500 to-green-600'
    },
    {
      icon: '⚖️',
      title: 'Asistente Legal',
      description: 'Información legal orientativa y revisión de documentos.',
      link: '/legal',
      gradient: 'from-violet-500 to-purple-600'
    },
    {
      icon: '🛡️',
      title: 'Ciberseguridad',
      description: 'Herramientas de protección y análisis de seguridad.',
      link: '/cybersecurity',
      gradient: 'from-red-500 to-rose-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-background-dark font-display">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Header />
        
        <main>
          {/* HERO 1: PRECIO + AHORRO (Protagonista absoluto) */}
          <section className="py-12 sm:py-16">
            <div className="text-center mb-8">
              <span className="inline-block px-4 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-sm font-semibold mb-4">
                Plataforma operativa · Precios especiales de lanzamiento
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 dark:text-white mb-4">
                Reemplaza <span className="text-red-500 line-through">$148+</span> en herramientas
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-emerald-600">
                  por solo $25.99/mes
                </span>
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                Una sola plataforma. Múltiples modelos de IA. Gestión centralizada.
              </p>
            </div>

            {/* Pricing Hero Card */}
            <div className="max-w-4xl mx-auto">
              <div className="bg-gradient-to-r from-gray-900 to-gray-800 dark:from-[#1a2530] dark:to-[#283039] rounded-3xl p-8 text-white shadow-2xl">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  {/* Left: Price */}
                  <div className="text-center md:text-left">
                    <p className="text-sm text-gray-400 mb-2">Plan Estándar - Todo incluido</p>
                    <div className="flex items-baseline justify-center md:justify-start gap-2 mb-4">
                      <span className="text-5xl font-black">$25.99</span>
                      <span className="text-gray-400">/mes</span>
                    </div>
                    <div className="flex flex-wrap gap-2 justify-center md:justify-start mb-6">
                      <span className="px-3 py-1 bg-white/10 rounded-full text-xs" translate="no">GPT-5.2</span>
                      <span className="px-3 py-1 bg-white/10 rounded-full text-xs" translate="no">Claude 4</span>
                      <span className="px-3 py-1 bg-white/10 rounded-full text-xs" translate="no">Gemini</span>
                      <span className="px-3 py-1 bg-white/10 rounded-full text-xs" translate="no">DALL-E 3</span>
                    </div>
                    <Link
                      to="/register"
                      className="inline-block w-full md:w-auto px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold rounded-xl hover:opacity-90 transition-opacity text-center"
                    >
                      Comenzar Gratis →
                    </Link>
                    <p className="text-xs text-gray-500 mt-3">Sin tarjeta • 1,000 créditos gratis</p>
                  </div>

                  {/* Right: What you replace */}
                  <div className="bg-white/5 rounded-2xl p-5">
                    <p className="text-sm font-semibold text-red-400 mb-3">💸 Ejemplo de suscripciones que puedes reemplazar:</p>
                    <div className="space-y-2 text-sm">
                      {[
                        { name: "ChatGPT Plus", price: "$20" },
                        { name: "Claude Pro", price: "$20" },
                        { name: "Jasper", price: "$49" },
                        { name: "Zapier", price: "$29" },
                        { name: "Notion AI", price: "$10" },
                        { name: "DALL-E", price: "$20" },
                      ].map((item) => (
                        <div key={item.name} className="flex justify-between text-gray-300">
                          <span translate="no">{item.name}</span>
                          <span className="text-red-400">{item.price}/mes</span>
                        </div>
                      ))}
                      <div className="border-t border-white/20 pt-2 mt-2 flex justify-between font-bold">
                        <span>Total mensual estimado</span>
                        <span className="text-red-500">$148+/mes</span>
                      </div>
                    </div>
                    <div className="mt-4 text-center">
                      <span className="inline-block px-4 py-2 bg-green-500/20 text-green-400 rounded-lg font-bold">
                        Ahorro estimado hasta $122+/mes
                      </span>
                    </div>
                    <p className="text-[11px] text-gray-400 mt-3 text-center leading-relaxed">
                      Precios referenciales publicados por cada proveedor a la fecha de esta página (mayo 2026). Pueden variar según región, plan, tipo de cuenta y condiciones comerciales vigentes de cada servicio. Nombres comerciales propiedad de sus respectivos titulares.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* HERO 2: Comparativa Confrontacional */}
          <section className="py-12 bg-white dark:bg-[#1a2530] rounded-3xl mb-8">
            <div className="max-w-6xl mx-auto px-6">
              <div className="text-center mb-8">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  🔍 ¿Por qué seguir pagando de más?
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  Compara lo que obtienes en cada plataforma
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead>
                    <tr className="border-b-2 border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4">Función</th>
                      <th className="text-center py-3 px-4 bg-primary/10 text-primary font-bold">GPT-JDP</th>
                      <th className="text-center py-3 px-4 text-gray-500">Otras plataformas populares</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {[
                      { feature: "Múltiples modelos IA en una suscripción", gptjdp: "4+ modelos integrados", others: "Usualmente 1 por plataforma" },
                      { feature: "Automatización asistida por IA", gptjdp: "Módulos integrados", others: "Generalmente separada" },
                      { feature: "Capas internas de seguridad", gptjdp: "Protocolos propios activos", others: "Varía por proveedor" },
                      { feature: "Chatbot multi-idioma", gptjdp: "5 idiomas configurados", others: "Depende del plan" },
                      { feature: "Sistema de agentes", gptjdp: "En desarrollo", others: "Experimental en la mayoría" },
                      { feature: "Panel de gestión y trazabilidad", gptjdp: "Panel administrativo propio", others: "Variable" },
                    ].map((row) => (
                      <tr key={row.feature} className="border-b border-gray-100 dark:border-gray-800">
                        <td className="py-3 px-4 font-medium text-gray-700 dark:text-gray-300">{row.feature}</td>
                        <td className="py-3 px-4 text-center bg-primary/5">
                          <span className="text-green-600 font-semibold">{row.gptjdp}</span>
                        </td>
                        <td className="py-3 px-4 text-center text-gray-500">{row.others}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 4 Diferenciadores */}
              <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { icon: "🤖", title: "IA Generativa", desc: "Multi-modelo" },
                  { icon: "⚡", title: "Automatización", desc: "Workflows IA" },
                  { icon: "🛡️", title: "Seguridad", desc: "Protocolos propios" },
                  { icon: "🏢", title: "Empresarial", desc: "B2B-first" }
                ].map((item, i) => (
                  <div key={i} className="text-center p-4 bg-gray-50 dark:bg-[#283039] rounded-xl">
                    <span className="text-2xl block mb-2">{item.icon}</span>
                    <p className="font-bold text-gray-900 dark:text-white text-sm">{item.title}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Services Grid */}
          <section className="py-12">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3">
                Todo lo que necesitas, en un solo lugar
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Herramientas activas y listas para usar
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, index) => (
                <Link
                  key={index}
                  to={service.link}
                  className={`relative group bg-gradient-to-br ${service.gradient} rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-1`}
                >
                  {service.badge && (
                    <span className="absolute top-4 right-4 px-2 py-1 text-xs font-semibold bg-white/20 backdrop-blur-sm rounded-full">
                      {service.badge}
                    </span>
                  )}
                  
                  <span className="text-4xl block mb-4">{service.icon}</span>
                  <h3 className="text-lg font-bold mb-2">{service.title}</h3>
                  <p className="text-sm text-white/80 mb-4">{service.description}</p>
                  
                  <div className="flex items-center text-sm font-medium text-white/90 group-hover:text-white">
                    <span>Explorar</span>
                    <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* HERO 3: Programa de Referidos - Mensaje viral */}
          <section className="py-12 bg-gradient-to-r from-orange-500 to-pink-500 rounded-3xl mb-8 text-white">
            <div className="max-w-4xl mx-auto px-6 text-center">
              <h2 className="text-2xl sm:text-3xl font-bold mb-3">
                🎁 Programa de Referidos con Créditos Internos
              </h2>
              <p className="text-lg opacity-90 mb-8">
                Invita amigos y acumula <strong>créditos internos</strong> de la plataforma
              </p>

              <div className="grid sm:grid-cols-4 gap-4 mb-8">
                {[
                  { tier: "🥉 Bronce", credits: "+500 créditos/ref", bonus: "+500 créditos primer referido" },
                  { tier: "🥈 Plata", credits: "+750 créditos/ref", bonus: "+2,000 créditos bonus" },
                  { tier: "🥇 Oro", credits: "+1,000 créditos/ref", bonus: "+5,000 créditos bonus" },
                  { tier: "💎 Platino", credits: "+1,500 créditos/ref", bonus: "+15,000 créditos bonus" }
                ].map((item) => (
                  <div key={item.tier} className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                    <p className="font-bold text-lg mb-1">{item.tier.split(' ')[0]}</p>
                    <p className="text-sm font-medium">{item.tier.split(' ')[1]}</p>
                    <p className="text-white/80 text-sm mt-2">{item.credits}</p>
                    <p className="text-yellow-300 text-xs mt-1">{item.bonus}</p>
                  </div>
                ))}
              </div>

              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 inline-block mb-4">
                <p className="text-sm mb-1">💡 Ejemplo con 6 referidos en nivel Plata:</p>
                <p className="font-bold">6 × 750 = 4,500 créditos + 2,000 bonus = <span className="text-yellow-300">6,500 créditos internos</span></p>
                <p className="text-xs opacity-90 mt-1">Duración estimada según tu consumo real (ej.: si usas 200 créditos/día ≈ 32 días).</p>
              </div>

              <p className="text-[11px] opacity-80 mb-6 max-w-2xl mx-auto leading-relaxed">
                Las recompensas del programa corresponden a <strong>créditos internos</strong> canjeables dentro de GPT-JDP, no son dinero ni transferibles. La duración depende del consumo real de cada usuario y del módulo utilizado.{' '}
                <Link to="/referral-terms" className="underline hover:text-yellow-200">Ver condiciones del programa</Link>.
              </p>

              <div>
                <Link
                  to="/register"
                  className="inline-block px-8 py-4 bg-white text-orange-600 font-bold rounded-xl hover:bg-gray-100 transition-colors"
                >
                  Obtener Mi Código de Referido →
                </Link>
              </div>
            </div>
          </section>

          {/* HERO 4: QR + Compartir */}
          <section className="py-12 bg-gray-100 dark:bg-[#1a2530] rounded-3xl mb-8">
            <div className="max-w-4xl mx-auto px-6">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="text-center md:text-left">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                    📱 Comparte y Crece
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Descarga el código QR para compartir en redes sociales, presentaciones o material impreso.
                  </p>
                  <Link
                    to="/qr-info"
                    className="inline-block px-6 py-3 bg-primary text-white font-semibold rounded-xl hover:bg-primary/90 transition-colors"
                  >
                    Ver Toda la Información →
                  </Link>
                </div>
                <div className="text-center">
                  <div className="bg-white p-6 rounded-2xl inline-block shadow-lg">
                    <img 
                      src="https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=https://gpt-jdp.com/qr-info&bgcolor=ffffff&color=000000"
                      alt="QR Code GPT-JDP"
                      className="w-44 h-44"
                    />
                    <p className="text-sm text-gray-600 mt-3 font-semibold">gpt-jdp.com</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* COUNTER: Usuarios Activos */}
          {publicStats && (
            <section className="py-12 mb-8" data-testid="user-counter-section">
              <div className="text-center mb-8">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  La comunidad GPT-JDP crece
                </h2>
                <p className="text-gray-600 dark:text-gray-400">Profesionales que ya confian en nuestra plataforma</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
                <CounterCard value={publicStats.total_users} label="Usuarios Registrados" color="blue" />
                <CounterCard value={publicStats.total_subscribers} label="Suscriptores Activos" color="green" />
                <CounterCard value={publicStats.services_active} label="Servicios de IA" color="purple" />
                <CounterCard value={publicStats.ai_models} label="Modelos de IA" color="orange" />
              </div>
            </section>
          )}

          {/* TESTIMONIOS */}
          {testimonials.length > 0 && (
            <section className="py-12 bg-gray-100 dark:bg-[#1a2530] rounded-3xl mb-8" data-testid="testimonials-section">
              <div className="max-w-5xl mx-auto px-6">
                <div className="text-center mb-10">
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    Lo que dicen nuestros usuarios
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">Experiencias reales de profesionales como tu</p>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {testimonials.map(t => (
                    <div key={t.id} className="bg-white dark:bg-[#283039] rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow" data-testid={`testimonial-${t.id}`}>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center text-white font-bold text-sm">
                          {t.avatar_initials || t.name?.slice(0,2).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 dark:text-white text-sm">{t.name}</p>
                          {t.role && <p className="text-xs text-gray-500 dark:text-gray-400">{t.role}</p>}
                        </div>
                      </div>
                      <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed mb-3">"{t.text}"</p>
                      <div className="flex gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <svg key={i} className={`w-4 h-4 ${i < t.rating ? 'text-yellow-400' : 'text-gray-300 dark:text-gray-600'}`} fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                          </svg>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Final CTA */}
          <section className="py-12">
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 dark:from-[#0f1419] dark:to-[#1a2530] rounded-3xl p-8 sm:p-12 text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                ¿Listo para dejar de pagar de más?
              </h2>
              <p className="text-gray-300 mb-8 max-w-xl mx-auto">
                Únete a miles de profesionales que ya centralizaron su IA con GPT-JDP
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link 
                  to="/register" 
                  className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold rounded-xl hover:opacity-90 transition-opacity"
                >
                  Crear Cuenta Gratis
                </Link>
                <Link 
                  to="/pricing" 
                  className="px-8 py-4 bg-transparent text-white font-semibold rounded-xl border border-white/30 hover:bg-white/10 transition-colors"
                >
                  Ver Todos los Planes
                </Link>
              </div>
            </div>
          </section>
        </main>
        
        <Footer />
      </div>
    </div>
  );
};

const CounterCard = ({ value, label, color }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const target = value || 0;
    if (target === 0) return;
    const duration = 1500;
    const steps = 30;
    const increment = target / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(current));
    }, duration / steps);
    return () => clearInterval(timer);
  }, [value]);

  const colorMap = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-emerald-600',
    purple: 'from-purple-500 to-violet-600',
    orange: 'from-orange-500 to-pink-500'
  };

  return (
    <div className={`bg-gradient-to-br ${colorMap[color] || colorMap.blue} rounded-2xl p-6 text-white text-center shadow-lg`} data-testid={`counter-${color}`}>
      <p className="text-4xl font-black">{count.toLocaleString()}+</p>
      <p className="text-sm opacity-80 mt-1">{label}</p>
    </div>
  );
};

export default Home;
